import type { MetricGroupFormState } from './metric-group-form-helpers';

export interface MetricGroupFormFieldsProps {
  form: MetricGroupFormState;
  setField: <K extends keyof MetricGroupFormState>(key: K, value: MetricGroupFormState[K]) => void;
  titleError: string | null;
  linkTargetError: string | null;
  trailing?: React.ReactNode;
}

export function MetricGroupFormFields({
  form,
  setField,
  titleError,
  linkTargetError,
  trailing,
}: Readonly<MetricGroupFormFieldsProps>) {
  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Title">
          <input
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
            value={form.title}
            onChange={(e) => setField('title', e.target.value)}
            maxLength={100}
            aria-invalid={titleError !== null}
          />
          {titleError && <FieldError message={titleError} />}
        </Field>

        <Field label="Source badge">
          <input
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
            value={form.sourceBadge}
            onChange={(e) => setField('sourceBadge', e.target.value)}
            maxLength={50}
            placeholder="JIRA · GITHUB"
          />
        </Field>

        <Field label="Subtitle" className="md:col-span-2">
          <input
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
            value={form.subtitle}
            onChange={(e) => setField('subtitle', e.target.value)}
            maxLength={255}
          />
        </Field>

        <Field label="Metric list preview" className="md:col-span-2">
          <input
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
            value={form.metricListPreview}
            onChange={(e) => setField('metricListPreview', e.target.value)}
            placeholder="Cycle Time · Throughput · WIP"
          />
        </Field>

        <Field label="Link target" className="md:col-span-2">
          <input
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
            value={form.linkTarget}
            onChange={(e) => setField('linkTarget', e.target.value)}
            maxLength={255}
            placeholder='/metric-view  or  https://example.com  (empty hides the link)'
            aria-invalid={linkTargetError !== null}
          />
          {linkTargetError && <FieldError message={linkTargetError} />}
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-4 border-t border-border pt-3">
        <ToggleSwitch
          label="Visible on homepage"
          checked={form.visible}
          onChange={(v) => setField('visible', v)}
        />
        <ToggleSwitch
          label="Show NEW pill"
          checked={form.isNew}
          onChange={(v) => setField('isNew', v)}
        />
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Display order</span>
          <input
            type="number"
            className="w-20 rounded-md border border-border bg-background px-2 py-1 text-sm focus:border-primary focus:outline-none"
            value={form.displayOrder}
            min={0}
            onChange={(e) => setField('displayOrder', Number(e.target.value))}
          />
        </label>
        {trailing && (
          <>
            <div className="flex-1" />
            {trailing}
          </>
        )}
      </div>
    </>
  );
}

interface FieldProps {
  label: string;
  className?: string;
  children: React.ReactNode;
}

function Field({ label, className, children }: Readonly<FieldProps>) {
  return (
    <div className={className}>
      <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function FieldError({ message }: Readonly<{ message: string }>) {
  return <p className="mt-1 text-xs text-error" role="alert">{message}</p>;
}

interface ToggleSwitchProps {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}

function ToggleSwitch({ label, checked, onChange }: Readonly<ToggleSwitchProps>) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="text-foreground">{label}</span>
    </label>
  );
}
